// Practice data for the hands-on tutorials (tours.js): a pretend café, "Bunny
// Café", with its own products, supplies, suppliers, purchase orders, staff and
// shifts. Pure factories - no DOM, no Supabase. The controller swaps these into
// model.state while a practice tour runs (_startInventoryPractice /
// _startTimesheetPractice) and puts the business's real data back afterwards.
//
// Every id starts with "practice-" so a practice row is recognizable anywhere
// it might surface. Built fresh on every call, with dates relative to now, so
// a replay always starts from the same story.

const iso = (d) => d.toISOString();
const daysAgo = (n, h = 9, m = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(h, m, 0, 0);
  return d;
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// ── Inventory: "Bunny Café" ──────────────────────────────────────────────────
// The story the inventory tour tells: beans start comfortably above their
// reorder point (500 g); selling 40 lattes (18 g each = 720 g) drops them to
// 480 g, which is Low. Milk and cups stay OK so beans are the one alert.

export const PRACTICE_IDS = {
  latte: "practice-menu-latte",
  cappuccino: "practice-menu-cappuccino",
  croissant: "practice-menu-croissant",
  beans: "practice-inv-beans",
  milk: "practice-inv-milk",
  cups: "practice-inv-cups",
  croissantStock: "practice-inv-croissant",
  roaster: "practice-sup-roaster",
  dairy: "practice-sup-dairy",
  jon: "practice-staff-jon",
  maya: "practice-staff-maya",
  ana: "practice-staff-ana",
  jonOpenShift: "practice-shift-jon-open",
  anaRequest: "practice-req-ana",
  jonLeave: "practice-leave-jon",
};

export const PRACTICE_LATTE_SALE = { lattes: 40, perSale: 5 };

export function buildInventoryPractice() {
  const P = PRACTICE_IDS;
  const created = iso(daysAgo(30));

  const menuItems = [
    { _id: P.latte, itemName: "Latte", price: 4.5, category: "Coffee", imageURL: null, iconEmoji: "☕", _stock: "0", hasVariants: false, variants: [], description: "", status: "active", taxClass: "standard" },
    { _id: P.cappuccino, itemName: "Cappuccino", price: 4, category: "Coffee", imageURL: null, iconEmoji: "🫖", _stock: "0", hasVariants: false, variants: [], description: "", status: "active", taxClass: "standard" },
    { _id: P.croissant, itemName: "Croissant", price: 3.5, category: "Pastry", imageURL: null, iconEmoji: "🥐", _stock: "0", hasVariants: false, variants: [], description: "", status: "active", taxClass: "standard" },
  ];

  const suppliers = [
    { id: P.roaster, name: "Bean There Roasters", category: null, contactName: "Mia", phone: "555 0142", email: "orders@beanthere.example", address: null, leadTimeDays: 2, paymentTerms: "Net 15", minOrder: null, status: "preferred", notes: null, createdAt: created },
    { id: P.dairy, name: "Fresh Dairy Co.", category: null, contactName: "Leo", phone: "555 0199", email: null, address: null, leadTimeDays: 1, paymentTerms: "On delivery", minOrder: null, status: "active", notes: null, createdAt: created },
  ];

  const items = [
    { id: P.beans, name: "Coffee beans", unit: "g", quantity: 1200, lowStockThreshold: 500, linkedMenuItemId: null, sku: "BEAN-HOUSE", category: null, unitCost: 0.02, supplierId: P.roaster, createdAt: created, updatedAt: iso(daysAgo(6)) },
    { id: P.milk, name: "Oat milk", unit: "L", quantity: 14, lowStockThreshold: 3, linkedMenuItemId: null, sku: null, category: null, unitCost: 3, supplierId: P.dairy, createdAt: created, updatedAt: iso(daysAgo(6)) },
    { id: P.cups, name: "Paper cups", unit: "pc", quantity: 160, lowStockThreshold: 50, linkedMenuItemId: null, sku: "CUP-12OZ", category: null, unitCost: 0.15, supplierId: P.roaster, createdAt: created, updatedAt: iso(daysAgo(6)) },
    // A "Simple count" product: its auto item carries the product's name.
    { id: P.croissantStock, name: "Croissant", unit: "pc", quantity: 14, lowStockThreshold: 4, linkedMenuItemId: P.croissant, sku: null, category: null, unitCost: 1.1, supplierId: null, createdAt: created, updatedAt: iso(daysAgo(1)) },
  ];

  const link = (n, menuItemId, inventoryItemId, qty) => ({ id: `practice-link-${n}`, menuItemId, inventoryItemId, qty, variantLabel: null, variantOption: null });
  const links = [
    link(1, P.latte, P.beans, 18),
    link(2, P.latte, P.milk, 0.2),
    link(3, P.latte, P.cups, 1),
    link(4, P.cappuccino, P.beans, 18),
    link(5, P.cappuccino, P.milk, 0.12),
    link(6, P.cappuccino, P.cups, 1),
    link(7, P.croissant, P.croissantStock, 1),
  ];

  const itemSuppliers = [
    { id: "practice-isup-1", inventoryItemId: P.beans, supplierId: P.roaster, unitCost: 0.02, preferred: true },
    { id: "practice-isup-2", inventoryItemId: P.cups, supplierId: P.roaster, unitCost: 0.15, preferred: true },
    { id: "practice-isup-3", inventoryItemId: P.milk, supplierId: P.dairy, unitCost: 3, preferred: true },
  ];

  // Last week's delivery, already received - gives History and the PO list
  // something real-looking before the user makes their own order.
  const receivedAt = iso(daysAgo(6, 10, 30));
  const purchaseOrders = [{
    id: "practice-po-1",
    poNumber: 1,
    supplierId: P.roaster,
    supplierName: "Bean There Roasters",
    status: "received",
    expectedDate: ymd(daysAgo(6)),
    notes: null,
    shipping: 5,
    tax: null,
    createdByName: "Bunny Café",
    receiptUrl: null,
    createdAt: iso(daysAgo(8, 15)),
    sentAt: iso(daysAgo(8, 15, 5)),
    receivedAt,
    cancelledAt: null,
    lines: [
      { id: "practice-pol-1", inventoryItemId: P.beans, itemName: "Coffee beans", unit: "g", qty: 1000, unitCost: 0.02, createdAt: iso(daysAgo(8, 15)) },
      { id: "practice-pol-2", inventoryItemId: P.cups, itemName: "Paper cups", unit: "pc", qty: 100, unitCost: 0.15, createdAt: iso(daysAgo(8, 15)) },
    ],
  }];

  const adj = (n, inventoryItemId, delta, kind, createdAt, extra = {}) => ({
    id: `practice-adj-${n}`, inventoryItemId, delta, kind, note: null, saleId: null,
    receiptNumber: null, saleDate: null, staffName: "Bunny Café", createdAt, ...extra,
  });
  const adjustments = [
    adj(3, P.croissantStock, -2, "sale", iso(daysAgo(1, 8, 40)), { saleId: "practice-sale-1", receiptNumber: 1041, saleDate: iso(daysAgo(1, 8, 40)) }),
    adj(2, P.cups, 100, "po", receivedAt),
    adj(1, P.beans, 1000, "po", receivedAt),
  ];

  return {
    menuItems,
    menuCategories: ["Coffee", "Pastry"],
    items,
    links,
    suppliers,
    itemSuppliers,
    purchaseOrders,
    adjustments,
    nextPoNumber: 2,
    nextReceipt: 1042,
  };
}

// ── Timesheets: the Bunny Café team ──────────────────────────────────────────
// Last full week (Mon-Sun), so the story never depends on what day it is:
// Jon forgot to clock out on Thursday (open shift), Ana filed a correction for
// Wednesday (says she started at 9:00, not 9:20), and Jon has a vacation
// request pending for next Friday.

export function buildTimesheetPractice() {
  const P = PRACTICE_IDS;
  const now = new Date();
  const thisMonday = new Date(now);
  thisMonday.setHours(0, 0, 0, 0);
  thisMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const monday = new Date(thisMonday);
  monday.setDate(thisMonday.getDate() - 7);
  const day = (offset, h, m = 0) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + offset);
    d.setHours(h, m, 0, 0);
    return d;
  };

  const staff = [
    { id: P.maya, firstName: "Maya", lastName: "Santos", email: "maya@bunnycafe.example", isActive: true, role: "Manager" },
    { id: P.jon, firstName: "Jon", lastName: "Reyes", email: "jon@bunnycafe.example", isActive: true, role: "Barista" },
    { id: P.ana, firstName: "Ana", lastName: "Cruz", email: "ana@bunnycafe.example", isActive: true, role: "Barista" },
  ];
  const names = { [P.maya]: "Maya Santos", [P.jon]: "Jon Reyes", [P.ana]: "Ana Cruz" };

  let n = 0;
  const shift = (staffId, offset, inH, inM, outH, outM, { open = false, id = null } = {}) => {
    n += 1;
    const clockIn = day(offset, inH, inM);
    const breakStart = day(offset, inH + 3, 0);
    return {
      id: id ?? `practice-shift-${n}`,
      staffId,
      staffName: names[staffId],
      hourlyRate: null,
      clockedInAt: iso(clockIn),
      clockedOutAt: open ? null : iso(day(offset, outH, outM)),
      note: "",
      source: "clock",
      editedBy: null,
      approvedBy: null,
      breaks: [{ id: `practice-break-${n}`, startedAt: iso(breakStart), endedAt: iso(new Date(breakStart.getTime() + 30 * 60000)) }],
    };
  };

  const anaWed = shift(P.ana, 2, 9, 20, 15, 0, { id: "practice-shift-ana-wed" });
  const shifts = [
    shift(P.maya, 0, 8, 0, 16, 30), shift(P.ana, 0, 9, 0, 15, 0),
    shift(P.maya, 1, 8, 0, 16, 30), shift(P.jon, 1, 12, 0, 20, 0),
    shift(P.maya, 2, 8, 0, 16, 30), anaWed, shift(P.jon, 2, 12, 0, 20, 0),
    shift(P.maya, 3, 8, 0, 16, 30), shift(P.jon, 3, 12, 0, 20, 0, { open: true, id: P.jonOpenShift }),
    shift(P.maya, 4, 8, 0, 16, 30), shift(P.ana, 4, 9, 0, 15, 0), shift(P.jon, 4, 12, 0, 20, 0),
    shift(P.jon, 5, 10, 0, 18, 0),
  ].sort((a, b) => new Date(a.clockedInAt) - new Date(b.clockedInAt));

  const editRequests = [{
    id: P.anaRequest,
    staffId: P.ana,
    staffName: names[P.ana],
    shiftId: anaWed.id,
    proposedIn: iso(day(2, 9, 0)),
    proposedOut: iso(day(2, 15, 0)),
    proposedBreaks: null,
    reason: "I started at 9:00, the tablet didn't take my PIN until later.",
    createdAt: iso(day(3, 9, 5)),
    currentIn: anaWed.clockedInAt,
    currentOut: anaWed.clockedOutAt,
  }];

  // Next Friday (from today's week), so the request is always upcoming.
  const nextFriday = new Date(thisMonday);
  nextFriday.setDate(thisMonday.getDate() + 11);
  const leaveRequests = [
    { id: P.jonLeave, staffId: P.jon, staffName: names[P.jon], type: "vacation", startDate: ymd(nextFriday), endDate: ymd(nextFriday), reason: "Family trip", status: "pending", createdAt: iso(day(4, 18)), reviewedByName: null, reviewedAt: null, reviewNote: null },
    { id: "practice-leave-maya", staffId: P.maya, staffName: names[P.maya], type: "sick", startDate: ymd(day(-10, 0)), endDate: ymd(day(-10, 0)), reason: "", status: "approved", createdAt: iso(day(-10, 7)), reviewedByName: "Bunny Café", reviewedAt: iso(day(-10, 8)), reviewNote: null },
  ];

  const devices = [{ id: "practice-device-1", label: "Front counter iPad", activatedAt: iso(day(-20, 9)), lastSeenAt: iso(new Date(now.getTime() - 10 * 60000)) }];

  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const sunday = day(6, 0);
  return {
    staff,
    shifts,
    editRequests,
    leaveRequests,
    deletedShifts: [],
    devices,
    onShift: [{ staffId: P.maya, name: names[P.maya], clockedInAt: iso(new Date(now.getTime() - 2 * 3600000)), onBreak: false }],
    periodMeta: { type: "week", value: ymd(monday), label: `${fmt(monday)} – ${fmt(sunday)} (practice week)` },
  };
}
